import React, { useState, useEffect } from 'react';
import { IconSettings, IconX, IconCheck, IconMoon, IconSun, IconDeviceLaptop } from '@tabler/icons-react';

const COLORS = [
  { name: 'blue',   hex: '#206bc4' },
  { name: 'azure',  hex: '#4299e1' },
  { name: 'indigo', hex: '#4263eb' },
  { name: 'purple', hex: '#ae3ec9' },
  { name: 'pink',   hex: '#d6336c' },
  { name: 'red',    hex: '#d63939' },
  { name: 'orange', hex: '#f76707' },
  { name: 'yellow', hex: '#f59f00' },
  { name: 'lime',   hex: '#74b816' },
  { name: 'green',  hex: '#2fb344' },
  { name: 'teal',   hex: '#0ca678' },
  { name: 'cyan',   hex: '#17a2b8' },
];

// 폰트 그룹
const FONT_GROUPS = [
  {
    label: '고딕 / 현대체',
    fonts: [
      { value: 'SUIT',        label: 'SUIT',         preview: '가나다라 ABCD' },
      { value: 'GmarketSans', label: 'GmarketSans',  preview: '가나다라 ABCD' },
      { value: 'GongGothic',  label: 'GongGothic',   preview: '가나다라 ABCD' },
      { value: 'MinSans',     label: 'MinSans',      preview: '가나다라 ABCD' },
      { value: 'ROKAFSans',   label: 'ROKAFSans',    preview: '가나다라 ABCD' },
      { value: 'KOTRAGothic', label: 'KOTRAGothic',  preview: '가나다라 ABCD' },
      { value: 'SLEIGothic',  label: 'SLEIGothic',   preview: '가나다라 ABCD' },
      { value: 'GimpoGothic', label: 'GimpoGothic',  preview: '가나다라 ABCD' },
      { value: 'YUniverse',   label: 'YUniverse',    preview: '가나다라 ABCD' },
      { value: 'ONEMobile',   label: 'ONEMobile',    preview: '가나다라 ABCD' },
    ],
  },
  {
    label: '바탕 / 명조',
    fonts: [
      { value: 'NanumOTF',   label: 'Nanum OTF',    preview: '가나다라 ABCD' },
      { value: 'GimpoBatang',label: 'GimpoBatang',  preview: '가나다라 ABCD' },
      { value: 'ROKAFSlab',  label: 'ROKAFSlab',    preview: '가나다라 ABCD' },
    ],
  },
  {
    label: '손글씨 / 개성체',
    fonts: [
      { value: 'BMJUA',        label: 'BMJUA',       preview: '가나다라 ABCD' },
      { value: 'BMEuljiro',    label: 'BM을지로',    preview: '가나다라 ABCD' },
      { value: 'Cafe24Ssukssuk', label: '카페24쑥쑥', preview: '가나다라 ABCD' },
      { value: 'Cafe24Dongdong', label: '카페24동동', preview: '가나다라 ABCD' },
      { value: 'twayfly',      label: 'twayfly',     preview: '가나다라 ABCD' },
      { value: 'OTWelcome',    label: 'OTWelcome',   preview: '가나다라 ABCD' },
      { value: 'ImcreSoojin',  label: '임크레수진',  preview: '가나다라 ABCD' },
    ],
  },
];

const RADIUS = [
  { value: '0',  label: '0' },
  { value: '2',  label: '2' },
  { value: '4',  label: '4' },
  { value: '6',  label: '6' },
  { value: '8',  label: '8' },
];

const MAX_WIDTHS = [
  { value: '1320px', label: '1320', desc: '기본' },
  { value: '1600px', label: '1600', desc: '넓게' },
  { value: '1920px', label: '1920', desc: 'Full HD' },
  { value: '2560px', label: '2560', desc: '2K' },
  { value: '3200px', label: '3200', desc: '울트라와이드' },
  { value: '3840px', label: '3840', desc: '4K' },
  { value: '100%',   label: '100%', desc: '제한없음' },
];

const COMPONENT_SIZES = [
  { value: 'xs', label: '매우 작게' },
  { value: 'sm', label: '작게' },
  { value: 'default', label: '보통' },
  { value: 'lg', label: '크게' },
  { value: 'xl', label: '매우 크게' },
];

const DEFAULTS = { theme: 'auto', color: 'blue', font: 'SUIT', radius: '4', maxWidth: '1320px', componentSize: 'sm' };

export function loadSettings() {
  try {
    const s = localStorage.getItem('tabler-theme');
    return s ? { ...DEFAULTS, ...JSON.parse(s) } : { ...DEFAULTS };
  } catch { return { ...DEFAULTS }; }
}

function hexToRgb(hex) {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `${r}, ${g}, ${b}`;
}

export function applySettings(settings) {
  const html = document.documentElement;

  // 1. 다크/라이트 (auto = OS 설정 따름)
  const effectiveTheme = settings.theme === 'auto'
    ? (window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light')
    : settings.theme;
  html.setAttribute('data-bs-theme', effectiveTheme);

  // 2. 주 색상
  const colorHex = COLORS.find(c => c.name === settings.color)?.hex || COLORS[0].hex;
  html.style.setProperty('--tblr-primary', colorHex);
  html.style.setProperty('--tblr-primary-rgb', hexToRgb(colorHex));
  html.style.setProperty('--tblr-link-color', colorHex);

  // 3. 폰트 — CSS 변수 + body 직접 적용 (둘 다 해야 확실히 적용됨)
  const fontStack = `"${settings.font}", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif`;
  html.style.setProperty('--tblr-font-family-sans-serif', fontStack);
  html.style.setProperty('--tblr-body-font-family', fontStack);
  document.body.style.fontFamily = fontStack;

  // 4. 모서리 둥글기
  const radPx = `${settings.radius}px`;
  html.style.setProperty('--tblr-border-radius', radPx);
  html.style.setProperty('--tblr-border-radius-sm', `calc(${radPx} * 0.75)`);
  html.style.setProperty('--tblr-border-radius-lg', `calc(${radPx} * 1.5)`);

  // 5. 최대 너비
  html.style.setProperty('--app-max-width', settings.maxWidth || '1320px');
  if (settings.maxWidth === '100%') {
    html.classList.add('layout-full-width');
  } else {
    html.classList.remove('layout-full-width');
  }

  // 6. 컴포넌트 크기
  ['xs', 'sm', 'lg', 'xl'].forEach(s => html.classList.remove(`component-size-${s}`));
  if (settings.componentSize && settings.componentSize !== 'default') {
    html.classList.add(`component-size-${settings.componentSize}`);
  }

  document.body.className = 'antialiased';
}

export default function ThemeSettings() {
  const [open, setOpen] = useState(false);
  const [settings, setSettings] = useState(loadSettings);

  useEffect(() => { applySettings(settings); }, []);

  const update = (key, value) => {
    const next = { ...settings, [key]: value };
    setSettings(next);
    localStorage.setItem('tabler-theme', JSON.stringify(next));
    applySettings(next);
  };

  const reset = () => {
    setSettings({ ...DEFAULTS });
    localStorage.setItem('tabler-theme', JSON.stringify(DEFAULTS));
    applySettings(DEFAULTS);
  };

  const allFonts = FONT_GROUPS.flatMap(g => g.fonts);
  const currentFont = allFonts.find(f => f.value === settings.font);

  return (
    <>
      {/* 토글 버튼 */}
      <button
        className="btn btn-sm btn-primary"
        style={{
          position: 'fixed', bottom: 24, right: 24, zIndex: 1050,
          borderRadius: '50%', width: 48, height: 48, padding: 0,
          boxShadow: '0 4px 16px rgba(32,107,196,0.4)',
        }}
        onClick={() => setOpen(true)}
        title="테마 설정"
      >
        <IconSettings size={22} />
      </button>

      {/* 오버레이 */}
      {open && (
        <div
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.3)', zIndex: 1060 }}
          onClick={() => setOpen(false)}
        />
      )}

      {/* 사이드 패널 */}
      <div style={{
        position: 'fixed', top: 0, right: 0, bottom: 0, width: 340,
        background: 'var(--tblr-bg-surface, #fff)',
        boxShadow: '-4px 0 24px rgba(0,0,0,0.15)',
        zIndex: 1070,
        transform: open ? 'translateX(0)' : 'translateX(100%)',
        transition: 'transform 0.3s cubic-bezier(.4,0,.2,1)',
        display: 'flex', flexDirection: 'column', overflowY: 'auto',
      }}>

        {/* 헤더 */}
        <div className="p-3 d-flex align-items-center border-bottom">
          <IconSettings size={20} className="me-2 text-primary" />
          <strong className="fs-4">테마 설정</strong>
          <button className="btn-close ms-auto" onClick={() => setOpen(false)} />
        </div>

        <div className="p-3 flex-fill">

          {/* 색상 모드 */}
          <div className="mb-4">
            <div className="form-label fw-semibold mb-2">색상 모드</div>
            <div className="row g-2">
              {[
                { value: 'auto',  label: '자동',   icon: <IconDeviceLaptop size={16} /> },
                { value: 'light', label: '화이트', icon: <IconSun size={16} /> },
                { value: 'dark',  label: '다크',   icon: <IconMoon size={16} /> },
              ].map(opt => (
                <div className="col-4" key={opt.value}>
                  <button
                    className={`btn btn-sm w-100 d-flex align-items-center justify-content-center gap-2
                      ${settings.theme === opt.value ? 'btn-primary' : 'btn-outline-secondary'}`}
                    onClick={() => update('theme', opt.value)}
                  >
                    {opt.icon} {opt.label}
                    {settings.theme === opt.value && <IconCheck size={14} />}
                  </button>
                </div>
              ))}
            </div>
          </div>

          {/* 주 색상 */}
          <div className="mb-4">
            <div className="form-label fw-semibold mb-2">주 색상</div>
            <div className="d-flex flex-wrap gap-2">
              {COLORS.map(c => (
                <button
                  key={c.name}
                  title={c.name}
                  onClick={() => update('color', c.name)}
                  style={{
                    width: 30, height: 30, borderRadius: '50%',
                    background: c.hex, border: 'none', cursor: 'pointer',
                    outline: settings.color === c.name ? `3px solid ${c.hex}` : 'none',
                    outlineOffset: 2,
                    transform: settings.color === c.name ? 'scale(1.2)' : 'scale(1)',
                    transition: 'transform 0.15s, outline 0.15s',
                  }}
                />
              ))}
            </div>
            <div className="text-muted small mt-1" style={{ color: COLORS.find(c => c.name === settings.color)?.hex }}>
              ● {settings.color}
            </div>
          </div>

          {/* 폰트 */}
          <div className="mb-4">
            <div className="form-label fw-semibold mb-2">
              폰트
              <span className="ms-2 text-muted small fw-normal"
                style={{ fontFamily: `"${settings.font}", sans-serif` }}>
                현재: {currentFont?.label}
              </span>
            </div>

            {FONT_GROUPS.map(group => (
              <div key={group.label} className="mb-3">
                <div className="text-muted small fw-semibold mb-1"
                  style={{ fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  {group.label}
                </div>
                <div className="d-flex flex-column gap-1">
                  {group.fonts.map(f => (
                    <button
                      key={f.value}
                      className={`btn btn-sm text-start ${settings.font === f.value ? 'btn-primary' : 'btn-outline-secondary'}`}
                      onClick={() => update('font', f.value)}
                      style={{ fontFamily: `"${f.value}", sans-serif` }}
                    >
                      <div className="d-flex align-items-center justify-content-between">
                        <div>
                          <span className="fw-medium">{f.label}</span>
                          <span className="ms-2 opacity-75 small">{f.preview}</span>
                        </div>
                        {settings.font === f.value && <IconCheck size={14} />}
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>

          {/* 모서리 둥글기 */}
          <div className="mb-4">
            <div className="form-label fw-semibold mb-2">모서리 둥글기</div>
            <div className="d-flex gap-2">
              {RADIUS.map(r => (
                <button
                  key={r.value}
                  title={`${r.value}px`}
                  className={`btn btn-sm flex-fill ${settings.radius === r.value ? 'btn-primary' : 'btn-outline-secondary'}`}
                  style={{ borderRadius: `${r.value}px` }}
                  onClick={() => update('radius', r.value)}
                >
                  {r.value}px
                </button>
              ))}
            </div>
          </div>

          {/* 컴포넌트 크기 */}
          <div className="mb-4">
            <div className="form-label fw-semibold mb-2">컴포넌트 크기</div>
            <div className="d-flex gap-2">
              {COMPONENT_SIZES.map(s => (
                <button
                  key={s.value}
                  className={`btn btn-sm flex-fill ${settings.componentSize === s.value ? 'btn-primary' : 'btn-outline-secondary'}`}
                  onClick={() => update('componentSize', s.value)}
                >
                  {s.label}
                  {settings.componentSize === s.value && <IconCheck size={14} className="ms-1" />}
                </button>
              ))}
            </div>
          </div>

          {/* 최대 너비 */}
          <div className="mb-4">
            <div className="form-label fw-semibold mb-2">
              최대 너비
              <span className="ms-2 badge bg-primary-lt fw-normal">
                {settings.maxWidth}
              </span>
            </div>
            <div className="d-flex flex-column gap-2">
              {MAX_WIDTHS.map(w => (
                <button
                  key={w.value}
                  className={`btn btn-sm d-flex align-items-center justify-content-between
                    ${settings.maxWidth === w.value ? 'btn-primary' : 'btn-outline-secondary'}`}
                  onClick={() => update('maxWidth', w.value)}
                >
                  <span className="fw-medium">{w.label}px</span>
                  <span style={{ opacity: 0.75, fontSize: '0.75rem' }}>{w.desc}</span>
                  {settings.maxWidth === w.value && <IconCheck size={14} />}
                </button>
              ))}
            </div>
            {/* 실시간 미리보기 바 */}
            <div className="mt-2 rounded overflow-hidden"
              style={{ height: 6, background: 'var(--tblr-border-color, #e6e7e9)' }}>
              <div style={{
                height: '100%',
                background: 'var(--tblr-primary, #206bc4)',
                width: settings.maxWidth === '100%' ? '100%'
                  : `${Math.min(100, parseInt(settings.maxWidth) / 25.6)}%`,
                transition: 'width 0.3s ease',
                borderRadius: 3,
              }} />
            </div>
            <div className="text-muted small mt-1 text-center">
              {settings.maxWidth === '100%' ? '화면 전체 너비 사용' : `최대 ${settings.maxWidth} 사용`}
            </div>
          </div>

        </div>

        {/* 초기화 */}
        <div className="p-3 border-top">
          <button className="btn btn-sm btn-outline-danger w-100" onClick={reset}>
            기본값으로 초기화
          </button>
        </div>
      </div>
    </>
  );
}
